import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminGetAllCompanies, adminGetCompanyFull } from '../services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Building2, Users, Mail, Eye, CheckCircle2, XCircle, MinusCircle, LogOut } from 'lucide-react';

// Logo ICL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a3de8ce8-9ebc-47a8-b532-221c9e0f1005/artifacts/0dlj7jep_LOGO%20ICL.png";

const AdminPage = ({ onBack }) => {
  const { user, logout } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetail, setCompanyDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await adminGetAllCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
    setLoading(false);
  };

  const viewCompanyDetail = async (company) => {
    setSelectedCompany(company);
    setDialogOpen(true);
    setDetailLoading(true);
    try {
      const data = await adminGetCompanyFull(company.id);
      setCompanyDetail(data);
    } catch (error) {
      console.error('Error loading company detail:', error);
    }
    setDetailLoading(false);
  };

  const getStatusColor = (percentage) => {
    if (percentage >= 85) return 'emerald';
    if (percentage >= 70) return 'amber';
    return 'rose';
  };

  const getStatusBadge = (percentage) => {
    const color = getStatusColor(percentage);
    return (
      <Badge className={`status-${color === 'emerald' ? 'high' : color === 'amber' ? 'medium' : 'low'}`}>
        {percentage}%
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onBack} data-testid="admin-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ICL Logo" className="h-10 w-auto" />
              <div>
                <span className="text-lg font-bold tracking-tight block">Panel de Administración</span>
                <span className="text-xs text-slate-500">Instituto de Cumplimiento Laboral</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
              Admin
            </Badge>
            <span className="text-sm text-slate-600">{user?.name}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-slate-600 hover:text-slate-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Empresas Registradas</h1>
          <p className="text-slate-600">Vista completa de todas las empresas y su estado de cumplimiento</p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardDescription>Total Empresas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{companies.length}</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardDescription>Cumplimiento Alto (&ge;85%)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">
                {companies.filter(c => c.compliance_percentage >= 85).length}
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardDescription>Cumplimiento Medio (70-84%)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">
                {companies.filter(c => c.compliance_percentage >= 70 && c.compliance_percentage < 85).length}
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardDescription>Cumplimiento Bajo (&lt;70%)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-rose-600">
                {companies.filter(c => c.compliance_percentage < 70).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Companies table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Listado de Empresas</CardTitle>
            <CardDescription>Haz clic en "Ver detalles" para ver el cuestionario completo</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : companies.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No hay empresas registradas</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>CIF</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead>Empleados</TableHead>
                    <TableHead>Cumplimiento</TableHead>
                    <TableHead>Fecha Registro</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell className="mono text-sm">{company.cif || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{company.user_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>{company.sector || '-'}</TableCell>
                      <TableCell>
                        {company.employees_count ? (
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-slate-400" />
                            {company.employees_count}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(company.compliance_percentage)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(company.created_at).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => viewCompanyDetail(company)}
                          data-testid={`view-company-${company.id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Ver detalles
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Company detail dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-3">
              <Building2 className="w-5 h-5" />
              {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription>
              Usuario: {companyDetail?.user?.email} ({companyDetail?.user?.name})
            </DialogDescription>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : companyDetail && (
            <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(95vh - 120px)' }}>
              {/* Stats summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-slate-900">{companyDetail.stats.percentage}%</div>
                  <div className="text-xs text-slate-500">Cumplimiento</div>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-emerald-600">{companyDetail.stats.compliant}</div>
                  <div className="text-xs text-slate-500">Cumple</div>
                </div>
                <div className="bg-rose-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-rose-600">{companyDetail.stats.non_compliant}</div>
                  <div className="text-xs text-slate-500">No cumple</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-slate-400">{companyDetail.stats.not_applicable}</div>
                  <div className="text-xs text-slate-500">N/A</div>
                </div>
              </div>

              {/* Areas - All expanded by default */}
              <div className="space-y-4">
                {companyDetail.areas.map((area) => {
                  const color = getStatusColor(area.percentage);
                  return (
                    <div key={area.id} className="border border-slate-200 rounded-lg overflow-hidden">
                      {/* Area header */}
                      <div className={`flex items-center justify-between p-3 ${
                        color === 'emerald' ? 'bg-emerald-50' : 
                        color === 'amber' ? 'bg-amber-50' : 'bg-rose-50'
                      }`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded flex items-center justify-center text-sm font-bold text-white ${
                            color === 'emerald' ? 'bg-emerald-500' : 
                            color === 'amber' ? 'bg-amber-500' : 'bg-rose-500'
                          }`}>
                            {area.code}
                          </div>
                          <span className="font-medium text-slate-900">{area.name}</span>
                        </div>
                        <Badge className={`status-${color === 'emerald' ? 'high' : color === 'amber' ? 'medium' : 'low'}`}>
                          {area.percentage}%
                        </Badge>
                      </div>
                      {/* Obligations - always visible */}
                      <div className="p-3 space-y-2">
                        {area.obligations.map((obl) => (
                          <div 
                            key={obl.id}
                            className={`flex items-start gap-3 p-3 rounded-lg border ${
                              obl.response === 'SI' ? 'bg-emerald-50 border-emerald-200' :
                              obl.response === 'NO' ? 'bg-rose-50 border-rose-200' :
                              obl.response === 'NA' ? 'bg-slate-50 border-slate-200' :
                              'bg-amber-50 border-amber-200'
                            }`}
                          >
                            <div className="flex-shrink-0">
                              {obl.response === 'SI' && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                              {obl.response === 'NO' && <XCircle className="w-5 h-5 text-rose-600" />}
                              {obl.response === 'NA' && <MinusCircle className="w-5 h-5 text-slate-400" />}
                              {!obl.response && <span className="w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center text-white text-xs">?</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="mono text-xs font-bold text-slate-500">{obl.code}</span>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                  obl.response === 'SI' ? 'bg-emerald-200 text-emerald-800' :
                                  obl.response === 'NO' ? 'bg-rose-200 text-rose-800' :
                                  obl.response === 'NA' ? 'bg-slate-200 text-slate-600' :
                                  'bg-amber-200 text-amber-800'
                                }`}>
                                  {obl.response || 'PENDIENTE'}
                                </span>
                              </div>
                              <p className="text-sm text-slate-700">{obl.description}</p>
                              {obl.notes && (
                                <p className="text-xs text-slate-500 mt-1 italic">Notas: {obl.notes}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
